import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp, query, collection, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { db } from '../firebaseConfig';
import { getAgeString } from '../utils/dateHelpers';

const Profile = () => {
  const { userId } = useParams(); // Retrieve the userId from the URL
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(null);

  useEffect(() => {
    if (!userId) {
      alert('No user ID provided.');
      window.location.href = '/auth';
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const docRef = doc(db, 'users', userId);
        let docSnap = await getDoc(docRef);

        // Read-after-write lag: brief retry before giving up.
        if (!docSnap.exists()) {
          await new Promise((r) => setTimeout(r, 1000));
          docSnap = await getDoc(docRef);
        }

        if (docSnap.exists()) {
          setUserData(docSnap.data());
          return;
        }

        // Recovery: if this is the signed-in user's own profile and the doc
        // is truly missing, we can't safely guess their userType (sitter vs
        // parent). Sign them out and send them back through signup so they
        // pick a role explicitly — silent guesses locked parents out before.
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === userId) {
          console.warn('Profile doc missing for current user — signing out to force clean re-signup.');
          try {
            const { signOut } = await import('firebase/auth');
            await signOut(auth);
          } catch (e) {
            console.warn('Signout during recovery failed:', e.message);
          }
          alert('We could not find your profile. Please sign up again to select your account type.');
          window.location.href = '/auth';
          return;
        }

        alert('Profile not found!');
        window.location.href = '/auth';
      } catch (error) {
        console.error('Error fetching profile:', error);
        alert('Error loading profile.');
        window.location.href = '/auth';
      } finally {
        setLoading(false);
      }
    };

    const fetchUserReviews = async () => {
      try {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('subjectId', '==', userId)
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewsData = reviewsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setReviews(reviewsData);
        const ratedReviews = reviewsData.filter((review) => {
          const rating = Number(review.rating);
          return !isNaN(rating) && rating > 0;
        });
        const totalRating = ratedReviews.reduce((sum, review) => sum + Number(review.rating), 0);
        const avgRating = ratedReviews.length > 0
          ? parseFloat((totalRating / ratedReviews.length).toFixed(1))
          : null;
        setAverageRating(avgRating);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };

    // Wait for Firebase Auth to rehydrate before reading anything.
    // Reading auth.currentUser synchronously at mount races the IndexedDB
    // restore right after signup's hard-navigation redirect and can leave
    // the loading spinner stuck.
    let started = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (started) return;
      started = true;
      if (!user) {
        window.location.href = '/auth';
        return;
      }
      fetchUserProfile();
      fetchUserReviews();
    });

    return () => unsubscribe();
  }, [userId]);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`text-xl ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  const handleEditProfile = () => {
    window.location.href = '/profile/edit'; // Navigate to the Edit Profile page
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-spin flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
        <p className="text-slate-600 font-medium">Loading profile...</p>
      </div>
    </div>
  );
  if (!userData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-800 to-indigo-800 bg-clip-text text-transparent mb-2">
            {userData.userType === 'sitter' ? 'Sitter Profile' : 'Parent Profile'}
          </h1>
          <p className="text-slate-600 text-lg">
            {userData.userType === 'sitter' ? 'Professional childcare provider' : 'Family seeking quality care'}
          </p>
        </div>

        {/* Main Profile Card */}
        <div className="bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg overflow-hidden mb-8">
          {/* Profile Header */}
          <div className="px-8 py-6 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50">
            <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
              {/* Profile Picture */}
              <div className="flex-shrink-0">
                {userData.profilePictureURL ? (
                  <div className="relative group">
                    <img
                      src={userData.profilePictureURL}
                      alt="Profile"
                      className="w-48 h-48 lg:w-64 lg:h-64 rounded-2xl object-cover shadow-lg border-4 border-white group-hover:border-blue-200 transition-all duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                ) : (
                  <div className="w-48 h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center shadow-lg">
                    <div className="text-center">
                      <svg className="w-16 h-16 text-slate-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-slate-500 text-sm font-medium">No Picture</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-grow text-center lg:text-left">
                <h2 className="text-3xl font-bold text-slate-800 mb-3">{userData.name}</h2>
                
                {/* Contact Information (only visible to profile owner) */}
                {auth.currentUser?.uid === userId && (
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span className="text-slate-600">{userData.address}</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-slate-600">{userData.email}</span>
                    </div>
                  </div>
                )}

                {/* Bio */}
                {(userData.bio || (userData.userType === 'sitter' && userData.dateOfBirth)) && (
                  <div className="mb-6 max-w-2xl">
                    {userData.userType === 'sitter' && userData.dateOfBirth && (
                      <p className="text-slate-700 font-semibold mb-2">
                        Age: {getAgeString(userData.dateOfBirth)} old
                      </p>
                    )}
                    {userData.bio && (
                      <p className="text-slate-600 leading-relaxed">
                        {userData.bio}
                      </p>
                    )}
                  </div>
                )}

                {/* Edit Profile Button */}
                {auth.currentUser?.uid === userId && (
                  <button
                    onClick={handleEditProfile}
                    className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit Profile</span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="p-8">
            <div className="space-y-8">
              {/* Sitter Specific Information */}
              {userData.userType === 'sitter' && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center space-x-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.815-8.964-2.222m2.964 2.222L21 13.255m0 0a23.931 23.931 0 01-9 3.222m9-3.222H3" />
                    </svg>
                    <span>Professional Experience</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Experience */}
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="font-semibold text-blue-800">Years of Experience</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-900">{userData.experience || '0'}</p>
                    </div>

                    {/* Certifications */}
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                        </div>
                        <span className="font-semibold text-emerald-800">Certifications</span>
                      </div>
                      
                      {userData.certifications?.length > 0 ? (
                        <div className="space-y-2">
                          {userData.certifications.map((certification, index) => {
                            const certName = typeof certification === 'string'
                              ? certification
                              : (typeof certification?.name === 'string'
                                  ? certification.name
                                  : (typeof certification?.name?.name === 'string' ? certification.name.name : ''));
                            const certURL = typeof certification === 'object' && typeof certification?.fileURL === 'string' ? certification.fileURL : '';
                            return (
                            <a
                              key={index}
                              href={certURL || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors duration-200 border border-slate-200"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-slate-700 font-medium">
                                  {certName || `Certification ${index + 1}`}
                                </span>
                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </div>
                            </a>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">No certifications listed</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Parent Specific Information */}
              {userData?.userType === "parent" && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-emerald-800 mb-4 flex items-center space-x-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>Family Information</span>
                  </h3>

                  {/* Children Ages */}
                  {((userData?.children && userData.children.length > 0) || (userData?.childrenAges && userData.childrenAges.length > 0)) ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userData.children ? (
                        userData.children.map((child, index) => (
                          <div
                            key={child.id || index}
                            className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                <span className="text-emerald-600 font-bold text-lg">{index + 1}</span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-emerald-800">
                                  {child.name || `Child ${index + 1}`}
                                </h4>
                                <p className="text-emerald-700">
                                  {child.dateOfBirth ? getAgeString(child.dateOfBirth) : 'Age not available'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        userData.childrenAges.map((age, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                <span className="text-emerald-600 font-bold text-lg">{index + 1}</span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-emerald-800">Child {index + 1}</h4>
                                <p className="text-emerald-700">
                                  {age.value} {age.unit} old
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 mx-auto mb-3 bg-emerald-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </div>
                      <p className="text-emerald-600 font-medium">No children listed</p>
                    </div>
                  )}

                  {/* Payment Method */}
                  {userData?.cardLast4 && auth.currentUser?.uid === userId && (
                    <div className="mt-6 bg-white rounded-xl p-4 border border-emerald-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-emerald-800">Saved Payment Method</h4>
                          <p className="text-emerald-700">Card ending in **** {userData.cardLast4}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg overflow-hidden">
          {/* Reviews Header */}
          <div className="px-8 py-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200/50">
            <h3 className="text-2xl font-bold text-amber-800 flex items-center space-x-2">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <span>Reviews & Ratings</span>
            </h3>
          </div>

          <div className="p-8">
            {/* Average Rating */}
            {averageRating && (
              <div className="mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6 max-w-md">
                <div className="flex items-center space-x-4">
                  <div className="text-amber-500 text-4xl">
                    {renderStars(Math.round(averageRating))}
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-800">
                      {averageRating} out of 5
                    </p>
                    <p className="text-amber-600">
                      {reviews.length} review{reviews.length !== 1 && 's'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews List — hide reviews without a valid star rating */}
            {(() => {
              const visibleReviews = reviews.filter((r) => {
                const rating = Number(r.rating);
                return !isNaN(rating) && rating > 0;
              });
              return visibleReviews.length > 0 ? (
              <div className="space-y-6">
                {visibleReviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        {review.reviewerProfilePicture ? (
                          <img
                            src={review.reviewerProfilePicture}
                            alt={review.reviewerName}
                            className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800">{review.reviewerName}</h4>
                          <div className="flex items-center space-x-1 mt-1">
                            {renderStars(review.rating)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-slate-700 leading-relaxed">{review.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-slate-600 mb-2">No reviews yet</h4>
                <p className="text-slate-500">
                  This profile doesn't have any reviews yet.
                </p>
              </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
